import React, { useEffect, useRef } from "react";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItemCategory } from "../itemcategory/itemCategoryModel";
import { IItem } from "../item/itemModel";
import FormTextArea from "../../components/formFields/FormTextArea";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import AppDialog, { CustomAppDialog } from "../../components/dialog/Dialog";
import { ImCross } from "react-icons/im";
import { closeDialog } from "../../components/dialog/dialogSlice";

import OrderSelectionDialog from "./BandhaiOrderSelectionDialog";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import {
  closeBandhaiReceiptChecklistDialog,
  closeBandhaiReceiptViewImageDialog,
  openBandhaiReceiptChecklistDialog,
  openBandhaiReceiptOrderSelectItemDialog,
  openBandhaiReceiptViewImageDialog,
  setBandhaiReceiptOrderItemIndex,
  setBandhaiReceiptOrderSave,
  setBandhaiReceiptPreviousOrder,
} from "./bandhaiReceiptSlice";
import BandhaiReceiptApprovalItemsArray from "./BandhaiReceiptApprovalItemsArray";
import BandhaiReceiptUsedByKarigarItemsArray from "./BandhaiReceiptUsedByKarigarItemsArray";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { FaRegEye } from "react-icons/fa";
import { setOrderImageViewPayload } from "../order/orderSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import AlertBox from "../../components/common/AlertBox";
import Slider from "react-slick";
import { findProductImageByProcess } from "../item/services/item.services";
import { PROCESS_ID } from "../../_contstants/common";
import BandhaiReceiptChargesArray from "./BandhaiReceiptChargesArray";
import BandhaiGoldReceiveArray from "./BandhaiGoldReceiveArray";
import { findByOrderNoBandhaiReceipt } from "./services/bandhaiReceipt.services";
import { formatWeight } from "../../_helper/helper";

interface ChangeWatchersProps {
  formRenderProps: FormRenderProps;
  fieldname: string;
}
const GhatOrderApprovalItemsChangeWatcher: React.FC<ChangeWatchersProps> = ({
  formRenderProps,
  fieldname,
}) => {
  const UnitList = useAppSelector((state) => state.unit.UnitList);
  const approval_items = formRenderProps.valueGetter(
    `${fieldname}.approval_items`,
  );
  const item_used_by_kariagr = formRenderProps.valueGetter(
    `${fieldname}.item_used_by_kariagr`,
  );
  const bandhai_charges = formRenderProps.valueGetter(
    `${fieldname}.bandhai_charges`,
  );

  useEffect(() => {
    if (!approval_items && !item_used_by_kariagr && !bandhai_charges) return;

    let total_stone_weight = 0;
    let hasChanges = false;
    const updates: any = {};

    // Process approval_items - create new array
    if (approval_items && approval_items?.length > 0) {
      const newApprovalItems = [...approval_items];
      let approvalItemsChanged = false;

      approval_items.forEach((approval_item: any, index: number) => {
        const find_unit = UnitList?.find(
          (unit: any) => unit.id === approval_item.unit_id,
        );
        const used_weight = approval_item.weight - approval_item.return_weight;
        const used_qty = approval_item.qty - approval_item.return_qty;

        total_stone_weight += approval_item.unit_id
          ? approval_item.type
            ? approval_item.type !== "SAMPLE"
              ? find_unit
                ? find_unit?.conversion_rate
                  ? (+find_unit?.conversion_rate || 0) * (+used_weight || 0)
                  : used_weight || 0
                : used_weight || 0
              : 0
            : 0
          : 0;

        // Check if values changed
        if (newApprovalItems[index].used_weight !== used_weight) {
          newApprovalItems[index] = {
            ...newApprovalItems[index],
            used_weight: used_weight,
          };
          approvalItemsChanged = true;
        }

        if (newApprovalItems[index].used_qty !== used_qty) {
          newApprovalItems[index] = {
            ...newApprovalItems[index],
            used_qty: used_qty,
          };
          approvalItemsChanged = true;
        }
      });

      if (approvalItemsChanged) {
        updates[`${fieldname}.approval_items`] = newApprovalItems;
        hasChanges = true;
      }
    }

    // Process item_used_by_kariagr - create new array
    if (item_used_by_kariagr && item_used_by_kariagr?.length > 0) {
      const newKarigarItems = [...item_used_by_kariagr];
      let karigarItemsChanged = false;

      item_used_by_kariagr.forEach((karigar_item: any, index: number) => {
        const find_unit = UnitList?.find(
          (unit: any) => unit.id === karigar_item.unit_id,
        );

        total_stone_weight += karigar_item.unit_id
          ? find_unit
            ? find_unit?.conversion_rate
              ? find_unit?.conversion_rate * (karigar_item.weight || 0)
              : karigar_item.weight || 0
            : karigar_item.weight || 0
          : 0;

        const rateCalcP = karigar_item.apply_on === "P";
        const rateCalcW = karigar_item.apply_on === "W";
        let total = 0;
        if (rateCalcP) {
          total = (karigar_item.qty || 0) * (karigar_item.rate || 0);
        }
        if (rateCalcW) {
          total = (karigar_item.weight || 0) * (karigar_item.rate || 0);
        }

        if (newKarigarItems[index].total !== total) {
          newKarigarItems[index] = {
            ...newKarigarItems[index],
            total: total,
          };
          karigarItemsChanged = true;
        }
      });

      if (karigarItemsChanged) {
        updates[`${fieldname}.item_used_by_kariagr`] = newKarigarItems;
        hasChanges = true;
      }
    }

    // Process bandhai_charges - create new array
    if (bandhai_charges && bandhai_charges?.length > 0) {
      const newChargesItems = [...bandhai_charges];
      let chargesChanged = false;

      bandhai_charges.forEach((charges: any, index: number) => {
        const calculatedTotal = (+charges.qty || 0) * (+charges.rate || 0);

        if (newChargesItems[index].total !== calculatedTotal) {
          newChargesItems[index] = {
            ...newChargesItems[index],
            total: calculatedTotal,
          };
          chargesChanged = true;
        }
      });

      if (chargesChanged) {
        updates[`${fieldname}.bandhai_charges`] = newChargesItems;
        hasChanges = true;
      }
    }

    // Update stone_weight
    const currentStoneWeight = formRenderProps.valueGetter(
      `${fieldname}.stone_weight`,
    );
    if (currentStoneWeight !== total_stone_weight) {
      updates[`${fieldname}.stone_weight`] = total_stone_weight;
      hasChanges = true;
    }

    // Apply all updates at once
    if (hasChanges && Object.keys(updates).length > 0) {
      Object.entries(updates).forEach(([path, value]) => {
        formRenderProps.onChange(path, { value });
      });
    }
  }, [
    // Keep your old dependency style but stringify for proper comparison
    JSON.stringify(
      approval_items?.map(
        (item: any) =>
          `${item.unit_id}-${item.weight}-${item.return_weight}-${item.return_qty}-${item.qty}-${item.type}`,
      ),
    ),
    JSON.stringify(
      item_used_by_kariagr?.map(
        (item: any) =>
          `${item.unit_id}-${item.weight}-${item.qty}-${item.rate}-${item.apply_on}`,
      ),
    ),
    JSON.stringify(
      bandhai_charges?.map((item: any) => `${item.qty}-${item.rate}`),
    ),
    UnitList,
  ]);

  return null;
};

const BandhaiReceiptOrderEntryArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const location = useLocation();
  const bandhai_receipt_guid = location?.state?.bandhai_receipt_guid;
  const loading = useAppSelector((state) => state.bandhaireceipt.loading);
  const gridRef = useRef<any>(null);

  const fieldName = fieldArrayRenderProps.name;
  const BandhaiReceiptOrderItemIndex = useAppSelector(
    (state) => state.bandhaireceipt.BandhaiReceiptOrderItemIndex,
  );
  const BandhaiReceiptPreviousOrder = useAppSelector(
    (state) => state.bandhaireceipt.BandhaiReceiptPreviousOrder,
  );
  const BandhaiReceiptChecklistDialog = useAppSelector(
    (state) => state.bandhaireceipt.BandhaiReceiptChecklistDialog,
  );
  const formRenderProps = fieldArrayRenderProps.formRenderProps;
  const PurityList = useAppSelector((state) => state.purity?.PurityList);

  // const karigar_wastage =
  //   fieldArrayRenderProps.formRenderProps.valueGetter("karigar_wastage");
  const currentIndex =
    BandhaiReceiptOrderItemIndex === 999
      ? fieldArrayRenderProps.value?.length === 0
        ? 0
        : fieldArrayRenderProps.value?.length - 1
      : BandhaiReceiptOrderItemIndex;

  const ItemList = useAppSelector((state) => state.item.ItemList);
  const ItemCategoryList = useAppSelector(
    (state) => state.itemCategory.ItemCategoryList,
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        order_id: null,
        order_no: "",
        item_id: null,
        item_category_id: null,
        item_description: "",
        gross_weight: null,
        stone_weight: 0,
        net_weight: null,
        // purity: null,
        // wastage: karigar_wastage,
        // phine: null,
        approval_items: [],
        item_used_by_kariagr: [],
        bandhai_charges: [],
        bandhai_gold_receive: [],
      },
    });
  }, [fieldArrayRenderProps]);

  useEffect(() => {
    if (BandhaiReceiptOrderItemIndex === 999) {
      pushElementInarray();
    }
  }, [BandhaiReceiptOrderItemIndex]);

  const handleFormClose = (index: any) => {
    if (BandhaiReceiptOrderItemIndex === 999) {
      fieldArrayRenderProps.onRemove({ index: index });
      dispatch(closeDialog());
      dispatch(setBandhaiReceiptOrderItemIndex(null));
      dispatch(setBandhaiReceiptOrderSave(false));
    } else {
      // Create a deep copy to avoid reference issues
      const previousOrderCopy = BandhaiReceiptPreviousOrder
        ? JSON.parse(JSON.stringify(BandhaiReceiptPreviousOrder))
        : null;

      if (previousOrderCopy && previousOrderCopy.approval_items?.length) {
        previousOrderCopy.approval_items = previousOrderCopy.approval_items.map(
          (approvalItem: any) => {
            return {
              ...approvalItem,
              IssueDate: approvalItem.IssueDate
                ? moment(approvalItem.IssueDate).toDate()
                : null,
            };
          },
        );
      }

      if (previousOrderCopy && previousOrderCopy.bandhai_gold_receive?.length) {
        previousOrderCopy.bandhai_gold_receive =
          previousOrderCopy.bandhai_gold_receive.map(
            (bandhaiGoldReceive: any) => {
              return {
                ...bandhaiGoldReceive,
                ReceiveDate: bandhaiGoldReceive.ReceiveDate
                  ? moment(bandhaiGoldReceive.ReceiveDate).toDate()
                  : null,
              };
            },
          );
      }

      // Ensure bandhai_charges are properly structured when restoring
      if (previousOrderCopy && previousOrderCopy.bandhai_charges) {
        previousOrderCopy.bandhai_charges =
          previousOrderCopy.bandhai_charges.map((charge: any) => ({
            ...charge,
            total: (+charge.qty || 0) * (+charge.rate || 0),
          }));
      }

      fieldArrayRenderProps?.formRenderProps.onChange(`${fieldName}.${index}`, {
        value: previousOrderCopy,
      });

      dispatch(closeDialog());
      dispatch(setBandhaiReceiptOrderItemIndex(null));
      dispatch(setBandhaiReceiptOrderSave(false));
      dispatch(setBandhaiReceiptPreviousOrder(null));
    }
  };

  // const handleAddOrder = async (e: React.FormEvent) => {
  //   e.preventDefault();

  //   const isValid = hasRequired(fieldArrayRenderProps.formRenderProps.errors, [
  //     `bandhai_receipt_order.${currentIndex}.approval_items.`,
  //     `bandhai_receipt_order.${currentIndex}.item_used_by_kariagr.`,
  //     `bandhai_receipt_order.${currentIndex}.order_no`,
  //     `bandhai_receipt_order.${currentIndex}.item_category_id`,
  //     `bandhai_receipt_order.${currentIndex}.gross_weight`,
  //     `bandhai_receipt_order.${currentIndex}.net_weight`,
  //     `bandhai_receipt_order.${currentIndex}.purity`,
  //     `bandhai_receipt_order.${currentIndex}.item_id`,
  //   ]);

  //   await fieldArrayRenderProps.formRenderProps.onSubmit(e);
  //   if (isValid) {
  //     dispatch(closeDialog());
  //     dispatch(setBandhaiReceiptOrderItemIndex(null));
  //     dispatch(setBandhaiReceiptOrderSave(false));
  //   }
  // };

  const handleAddOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValidate =
      currentIndex !== null &&
      fieldArrayRenderProps.value[currentIndex]?.order_no &&
      fieldArrayRenderProps.value[currentIndex]?.item_category_id &&
      fieldArrayRenderProps.value[currentIndex]?.item_id &&
      fieldArrayRenderProps.value[currentIndex]?.net_weight &&
      fieldArrayRenderProps.value[currentIndex]?.gross_weight &&
      fieldArrayRenderProps.value[currentIndex]?.weight;

    const isValidApprovalItems =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].approval_items ||
        fieldArrayRenderProps.value[currentIndex].approval_items.length === 0 ||
        fieldArrayRenderProps.value[currentIndex].approval_items.every(
          (item: any) =>
            item?.type &&
            item?.item_id &&
            item?.weight &&
            item?.qty &&
            item?.unit_id,
        ));
    const isValidKarigarItems =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr ||
        fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr
          .length === 0 ||
        fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr.every(
          (item: any) =>
            item.item_id &&
            item.weight &&
            item.qty &&
            item.unit_id &&
            item.apply_on &&
            item.rate,
        ));
    const isValidCharges =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].bandhai_charges ||
        fieldArrayRenderProps.value[currentIndex].bandhai_charges.length ===
          0 ||
        fieldArrayRenderProps.value[currentIndex].bandhai_charges.every(
          (item: any) => item.item_id && item.qty,
        ));
    const isValidGoldReceive =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].bandhai_gold_receive ||
        fieldArrayRenderProps.value[currentIndex].bandhai_gold_receive
          .length === 0 ||
        fieldArrayRenderProps.value[currentIndex].bandhai_gold_receive.every(
          (item: any) => item.ReceiveDate && item.item_id && item.ReceiveWeight,
        ));
    await fieldArrayRenderProps.formRenderProps.onSubmit(e);
    if (
      isValidate &&
      isValidApprovalItems &&
      isValidKarigarItems &&
      isValidCharges &&
      isValidGoldReceive
    ) {
      if (fieldArrayRenderProps.value[currentIndex]?.id) {
        const getpayload = {
          order_id: fieldArrayRenderProps.value[currentIndex]?.order_id,
          bandhai_issue_id:
            fieldArrayRenderProps.value[currentIndex]?.bandhai_issue_id,
        };
        const { payload: res, meta } = await dispatch(
          findByOrderNoBandhaiReceipt(getpayload),
        );
        if (meta.requestStatus === "fulfilled" && res) {
          const updateField = (field: any, value: any) =>
            formRenderProps?.onChange(`${fieldName}.${currentIndex}.${field}`, {
              value,
            });
          if (PurityList && PurityList?.length > 0) {
            const purity = PurityList?.find(
              (item: any) => item.ID === res?.purity_id,
            );
            updateField("purity", purity?.purity);
            updateField("Carat", purity?.Carat);
          }
          updateField("item_id", res?.item_id);
          updateField("item_name", res?.item_name);
          updateField("order_id", res?.order_id);
          updateField("order_no", res?.order_no);
          updateField("ghat_id", res?.ghat_id);
          updateField("item_description", res?.order_process_description);
          updateField("Order_remark", res?.order_vepari_remark);
          updateField("issue_remarks", res?.order_issue_remarks);
          updateField("item_category_id", res?.item_category_id);
          updateField("weight", res?.weight);
          updateField("purity_id", res?.purity_id);
          updateField("item_image", res?.item_image);
          updateField("order_date", res?.order_date);
          updateField("delivery_date", res?.delivery_date);
          updateField("product_name", res?.product_name);
          updateField("category_name", res?.category_name);
          updateField("sub_category_name", res?.sub_category_name);
          updateField("order_estimate_weight", res?.order_estimate_weight);
          updateField("size", res?.size);
          updateField("color", res?.color);
          updateField("priority", res?.priority);
          updateField("priority", res?.priority);
          updateField("karigar_name", res?.karigar_name);
          updateField("given_date", res?.given_date);
          updateField("receive_date", res?.receive_date);
          updateField(
            "sizedetails",
            JSON.parse(JSON.stringify(res?.sizedetails))?.map((item: any) => ({
              title: item.title,
              value: item.value,
            })),
          );
          dispatch(openBandhaiReceiptChecklistDialog());
        } else {
          const updateField = (field: any, value: any) =>
            formRenderProps?.onChange(field, { value });
          updateField("item_id", null);
          updateField("item_name", "");
          updateField("order_id", null);
          updateField("order_no", null);
          updateField("ghat_id", null);
          updateField("item_description", "");
          updateField("item_category_id", null);
          updateField("weight", null);
          updateField("purity_id", null);
          updateField("item_description", null);
          updateField("item_image", null);
          updateField("approval_items", []);
          updateField("sizedetails", []);
        }
      } else {
        dispatch(openBandhaiReceiptChecklistDialog());
      }
    }
  };

  const handleCloseAndAdd = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValidate =
      currentIndex !== null &&
      fieldArrayRenderProps.value[currentIndex]?.order_no &&
      fieldArrayRenderProps.value[currentIndex]?.item_category_id &&
      fieldArrayRenderProps.value[currentIndex]?.item_id &&
      fieldArrayRenderProps.value[currentIndex]?.gross_weight;

    const isChecklistApproved =
      currentIndex !== null &&
      fieldArrayRenderProps.value[currentIndex]?.iscaret &&
      fieldArrayRenderProps.value[currentIndex]?.isdesign &&
      fieldArrayRenderProps.value[currentIndex]?.isgoldcolor &&
      fieldArrayRenderProps.value[currentIndex]?.isweight &&
      fieldArrayRenderProps.value[currentIndex]?.issize &&
      fieldArrayRenderProps.value[currentIndex]?.ispolishcolor &&
      fieldArrayRenderProps.value[currentIndex]?.iseverythingasdescription;

    const isValidApprovalItems =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].approval_items ||
        fieldArrayRenderProps.value[currentIndex].approval_items.length === 0 ||
        fieldArrayRenderProps.value[currentIndex].approval_items.every(
          (item: any) =>
            item?.type &&
            item?.item_id &&
            item?.weight &&
            item?.qty &&
            item?.unit_id,
        ));

    const isValidKarigarItems =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr ||
        fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr
          .length === 0 ||
        fieldArrayRenderProps.value[currentIndex].item_used_by_kariagr.every(
          (item: any) =>
            item.item_id &&
            item.weight &&
            item.qty &&
            item.unit_id &&
            item.apply_on &&
            item.rate,
        ));

    const isValidCharges =
      currentIndex !== null &&
      (!fieldArrayRenderProps.value[currentIndex].bandhai_charges ||
        fieldArrayRenderProps.value[currentIndex].bandhai_charges.length ===
          0 ||
        fieldArrayRenderProps.value[currentIndex].bandhai_charges.every(
          (item: any) => item.item_id && item.qty,
        ));

    await fieldArrayRenderProps.formRenderProps.onSubmit(e);
    if (
      isValidate &&
      isValidApprovalItems &&
      isValidKarigarItems &&
      isChecklistApproved &&
      isValidCharges
    ) {
      dispatch(closeBandhaiReceiptChecklistDialog());
      dispatch(closeDialog());
      dispatch(setBandhaiReceiptOrderItemIndex(null));
      dispatch(setBandhaiReceiptOrderSave(false));
    }
  };

  const handleSelectOrder = () => {
    dispatch(openBandhaiReceiptOrderSelectItemDialog());
  };

  const handleCloseChecklist = () => {
    dispatch(closeBandhaiReceiptChecklistDialog());
  };

  const handleOpenImageViewDialog = async (itemID: any) => {
    const payload = {
      process_id: PROCESS_ID.bandhai,
      item_id: itemID,
    };
    await dispatch(findProductImageByProcess(payload));
    dispatch(setOrderImageViewPayload(payload));
    dispatch(openBandhaiReceiptViewImageDialog());
  };

  return (
    <>
      {dialogName === "bandhai-order-entry-dialog" && (
        <AppDialog>
          <div
            style={{ width: "80vw", maxHeight: "95vh", position: "relative" }}
          >
            {loading && <LoadingPanel gridRef={gridRef} />}
            <OrderSelectionDialog
              formRenderProps={fieldArrayRenderProps.formRenderProps}
              fieldname={`${fieldName}.${currentIndex}`}
            />
            <CustomAppDialog open={BandhaiReceiptChecklistDialog}>
              <div
                className="enhanced-checklist-dialog"
                style={{ width: "60vw", minWidth: "300px" }}
              >
                <GridLayout gap={{ rows: 12, cols: 10 }}>
                  {/* Header */}
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingBottom: "12px",
                      borderBottom: "1px solid #e9ecef",
                    }}
                  >
                    <Typography.h4
                      style={{
                        margin: "0",
                        color: "#2c3e50",
                        fontWeight: "600",
                      }}
                    >
                      Quality Checklist
                    </Typography.h4>
                    <ImCross
                      className="enhanced-checklist-dialog__close-button"
                      style={{
                        fontSize: 20,
                        color: "white",
                        background: "#dc3545",
                        borderRadius: "50%",
                        padding: "6px",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        dispatch(closeBandhaiReceiptChecklistDialog())
                      }
                    />
                  </GridLayoutItem>

                  <div className="w-100">
                    <Typography.h5 style={{ margin: 0 }}>
                      Order Detail - No:
                      {currentIndex !== null &&
                        fieldArrayRenderProps.value?.[currentIndex]?.order_no}
                    </Typography.h5>
                    <table className="table table-striped table-bordered table-hover">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Category</th>
                          <th>Sub Category</th>
                          <th>Order Date</th>
                          <th>Delivery Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentIndex !== null && (
                          <tr>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.item_name
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.category_name
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.sub_category_name
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.order_date
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.delivery_date
                              }
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <thead>
                        <tr>
                          <th>Estimated Weight</th>
                          <th>Size</th>
                          <th>Purity</th>
                          <th>Color</th>
                          <th>Priority</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentIndex !== null && (
                          <tr>
                            <td>
                              {formatWeight(
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.order_estimate_weight,
                              )}
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.size
                              }
                            </td>
                            <td>
                              {" "}
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.Carat
                              }{" "}
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.purity
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.color
                              }
                            </td>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.priority
                              }
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>
                            <b>Remark :</b>
                            {
                              fieldArrayRenderProps.value?.[currentIndex]
                                ?.Order_remark
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <Typography.h5 style={{ margin: 0 }}>
                      Bandhai Process Detail
                    </Typography.h5>
                    <table className="table table-striped table-bordered table-hover">
                      <thead>
                        <tr>
                          <th>Karigar Name</th>
                          <th>Process Weight</th>
                          <th>Approx work Start Date</th>
                          <th>Received Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentIndex !== null && (
                          <tr>
                            <td>
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.karigar_name
                              }
                            </td>
                            <td>
                              {formatWeight(
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.weight,
                              )}
                            </td>
                            <td>
                              {moment(
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.given_date,
                                "YYYY-MM-DD",
                              ).format("DD/MM/YYYY")}
                            </td>
                            <td>
                              {moment(
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.receive_date,
                                "YYYY-MM-DD",
                              ).format("DD/MM/YYYY")}
                            </td>
                          </tr>
                        )}
                        {currentIndex !== null && (
                          <tr>
                            <td colSpan={5}>
                              <b>Remarks :</b>{" "}
                              {
                                fieldArrayRenderProps.value?.[currentIndex]
                                  ?.issue_remarks
                              }
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Checklist Items */}
                  <div style={{ display: "flex", gap: "1vw" }}>
                    <div
                      style={{
                        width: "34vw",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "start",
                      }}
                    >
                      <div className="w-100">
                        <Typography.h5 style={{ margin: 0 }}>
                          Size Details
                        </Typography.h5>
                        {currentIndex !== null && (
                          <table className="table table-striped table-bordered table-hover">
                            <thead>
                              <tr>
                                <th>Title</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fieldArrayRenderProps.value?.[
                                currentIndex
                              ]?.sizedetails?.map((item: any) => (
                                <tr>
                                  <td>{item?.title}</td>
                                  <td>{item?.value}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      <Button
                        type="button"
                        style={{ textTransform: "uppercase" }}
                        themeColor={"info"}
                        fillMode="solid"
                        disabled={
                          !fieldArrayRenderProps.formRenderProps.valueGetter(
                            `${fieldName}.${currentIndex}.order_no`,
                          )
                        }
                        onClick={() =>
                          handleOpenImageViewDialog(
                            fieldArrayRenderProps.formRenderProps.valueGetter(
                              `${fieldName}.${currentIndex}.item_id`,
                            ),
                          )
                        }
                      >
                        <FaRegEye style={{ fontSize: 24 }} /> View Image
                      </Button>
                      <ViewProductImages />
                    </div>
                    <div
                      className="enhanced-checklist-dialog__items"
                      style={{ width: "25vw" }}
                    >
                      {[
                        {
                          name: `${fieldName}.${currentIndex}.iscaret`,
                          title: "Caret",
                          label: "Caret",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.isdesign`,
                          title: "Design",
                          label: "Design",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.isgoldcolor`,
                          title: "Gold Color",
                          label: "Gold Color",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.isweight`,
                          title: "Weight (gm)",
                          label: "Weight (gm)",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.issize`,
                          title: "Size",
                          label: "Size",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.ispolishcolor`,
                          title: "Polish Color",
                          label: "Polish Color",
                        },
                        {
                          name: `${fieldName}.${currentIndex}.iseverythingasdescription`,
                          title: "Everything as Description",
                          label: "Everything as Description",
                        },
                      ].map((item, index) => (
                        <GridLayoutItem
                          key={item.name}
                          className="enhanced-checklist-dialog__item"
                          style={{
                            padding: "10px 12px",
                            borderRadius: "8px",
                            backgroundColor: "#f2f4ff",
                            marginBottom: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                            animationDelay: `${index * 0.1}s`,
                          }}
                        >
                          <Field
                            id={`fieldId-${index}`}
                            name={item.name}
                            title={item.title}
                            label={item.label}
                            size="large"
                            component={FormCheckbox}
                            validator={requiredValidator}
                            wrapperClassName="enhanced-checklist-dialog__checkbox"
                          />
                        </GridLayoutItem>
                      ))}
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingTop: "15px",
                      borderTop: "1px solid #e9ecef",
                      gap: "8px",
                    }}
                  >
                    <Field
                      name={`${fieldName}.${currentIndex}.isfinishorder`}
                      title={"Finish Order"}
                      label={"Finish Order"}
                      size="large"
                      component={FormCheckbox}
                      // validator={requiredValidator}
                      wrapperClassName="enhanced-checklist-dialog__checkbox"
                    />
                    <div>
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        // className="enhanced-checklist-dialog__button"

                        onClick={handleCloseChecklist}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        fillMode={"solid"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={(e) => handleCloseAndAdd(e)}
                      >
                        {!bandhai_receipt_guid ? "Submit" : "Submit"}
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </div>
            </CustomAppDialog>
            <GhatOrderApprovalItemsChangeWatcher
              formRenderProps={fieldArrayRenderProps.formRenderProps}
              fieldname={`${fieldName}.${currentIndex}`}
            />
            <GridLayout style={{ padding: 10 }} gap={{ rows: 10, cols: 10 }}>
              <GridLayoutItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography.h4>Add Order</Typography.h4>
                <ImCross
                  style={{
                    color: "white",
                    background: "red",
                    borderRadius: "50%",
                    padding: "5px",
                    fontSize: "24px",
                  }}
                  onClick={() => handleFormClose(currentIndex)}
                />
              </GridLayoutItem>
              <GridLayout cols={[{ width: "100%" }]}>
                <GridLayoutItem
                  style={{
                    minHeight: "30vh",
                    maxHeight: "80vh",
                    border: "2px solid #fba700",
                    borderRadius: "10px",
                    padding: "5px",
                    overflow: "scroll",
                  }}
                >
                  {/* <Card className="fieldarray-card"> */}
                  <GridLayout
                    style={{ marginRight: 30, padding: 10 }}
                    gap={{ rows: 10, cols: 10 }}
                    cols={[
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                      { width: "25%" },
                    ]}
                  >
                    <GridLayoutItem
                      colSpan={4}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Button
                        type="button"
                        fillMode={"solid"}
                        themeColor={"primary"}
                        style={{ width: "fit-content", padding: "5px" }}
                        onClick={handleSelectOrder}
                      >
                        Select Order
                      </Button>
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.order_no`}
                        label="Order No"
                        placeholder="i.e. 1542"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.item_category_id`}
                        label="Product Category"
                        placeholder="Select Product Category"
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        disabled={true}
                        options={ItemCategoryList?.map(
                          (itemcate: IItemCategory) => {
                            return {
                              value: itemcate.ID,
                              label: itemcate.category_name,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2} rowSpan={2}>
                      <Field
                        name={`${fieldName}.${currentIndex}.item_description`}
                        label="Product Description"
                        placeholder="Write Here..."
                        component={FormTextArea}
                        rows={4}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.item_id`}
                        label="Product"
                        placeholder="Select Product"
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        disabled={true}
                        options={ItemList?.map((item: IItem) => {
                          return {
                            value: item.id,
                            label: item.item_name,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`${fieldName}.${currentIndex}.weight`}
                        label="weight"
                        placeholder="Weight"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    {/* APPROVAL ITEMS ARRAY */}
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        overflowX: "hidden",
                        border: "1px dotted gray",
                        padding: "5px",
                        borderRadius: "8px",
                        marginTop: 20,
                      }}
                    >
                      <FieldArray
                        formRenderProps={fieldArrayRenderProps.formRenderProps}
                        component={BandhaiReceiptApprovalItemsArray}
                        name={`${fieldName}.${currentIndex}.approval_items`}
                      />
                    </GridLayoutItem>
                    {/* USED BY KARIGAR ITEMS ARRAY */}
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        overflowX: "hidden",
                        border: "1px dotted gray",
                        padding: "5px",
                        borderRadius: "8px",
                        marginTop: 20,
                        marginBottom: 10,
                      }}
                    >
                      <FieldArray
                        formRenderProps={fieldArrayRenderProps.formRenderProps}
                        component={BandhaiReceiptUsedByKarigarItemsArray}
                        name={`${fieldName}.${currentIndex}.item_used_by_kariagr`}
                      />
                    </GridLayoutItem>
                    {/* Bandhai receipt charges array */}
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        overflowX: "hidden",
                        border: "1px dotted gray",
                        padding: "5px",
                        borderRadius: "8px",
                        marginTop: 20,
                        marginBottom: 10,
                      }}
                    >
                      <FieldArray
                        formRenderProps={fieldArrayRenderProps.formRenderProps}
                        component={BandhaiReceiptChargesArray}
                        name={`${fieldName}.${currentIndex}.bandhai_charges`}
                      />
                    </GridLayoutItem>
                    {/* Gold return */}
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        overflowX: "hidden",
                        border: "1px dotted gray",
                        padding: "5px",
                        borderRadius: "8px",
                        marginTop: 20,
                        marginBottom: 10,
                      }}
                    >
                      <FieldArray
                        formRenderProps={fieldArrayRenderProps.formRenderProps}
                        component={BandhaiGoldReceiveArray}
                        name={`${fieldName}.${currentIndex}.bandhai_gold_receive`}
                      />
                    </GridLayoutItem>

                    <GridLayoutItem colSpan={4}>
                      <GridLayout
                        style={{ marginRight: 50 }}
                        gap={{ rows: 10, cols: 10 }}
                        cols={[
                          { width: "16.66%" },
                          { width: "16.66%" },
                          { width: "16.66%" },
                          { width: "16.66%" },
                          { width: "16.66%" },
                          { width: "16.66%" },
                        ]}
                      >
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.gross_weight`}
                            label="Gross Weight (gm)"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            astrike={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.stone_weight`}
                            label="Stone Weight (gm)"
                            placeholder="0.000"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            // astrike={true}
                          />
                        </GridLayoutItem>
                        {/* <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.net_weight`}
                            label="Net Weight (gm)"
                            placeholder="0.000"
                            format="n3"
                            disabled={true}
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            astrike={true}
                          />
                        </GridLayoutItem> */}
                        {/* <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.purity`}
                            label="Purity"
                            placeholder="0.00"
                            format="n2"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            astrike={true}
                            disabled={true}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.wastage`}
                            label="Wastage"
                            placeholder="0.00"
                            format="n2"
                            component={FormNumericTextField}
                          />
                        </GridLayoutItem>
                        <GridLayoutItem>
                          <Field
                            name={`${fieldName}.${currentIndex}.phine`}
                            label="Phine (gm)"
                            placeholder="0.00"
                            format="n2"
                            disabled={true}
                            component={FormNumericTextField}
                          />
                        </GridLayoutItem> */}
                      </GridLayout>
                    </GridLayoutItem>
                    {/* ACTION BUTTOM */}
                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        textAlign: "end",
                        marginTop: "20px",
                        marginRight: 20,
                        width: "100%",
                        display: "flex",
                        justifyContent: "end",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        type="button"
                        fillMode={"solid"}
                        themeColor={"primary"}
                        style={{ marginRight: 4 }}
                        onClick={(e) => handleAddOrder(e)}
                        // disabled={
                        //   !fieldArrayRenderProps.formRenderProps.allowSubmit
                        // }
                      >
                        Add
                      </Button>
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginRight: 4 }}
                        onClick={() => handleFormClose(currentIndex)}
                      >
                        Cancel
                      </Button>
                    </GridLayoutItem>
                  </GridLayout>
                  {/* </Card> */}
                </GridLayoutItem>
              </GridLayout>
            </GridLayout>
          </div>
        </AppDialog>
      )}
    </>
  );
};

const ViewProductImages = () => {
  const processByImageList = useAppSelector(
    (state) => state.item.ProcessByImageList,
  );
  const BandhaiReceiptViewImageDialog = useAppSelector(
    (state) => state.bandhaireceipt.BandhaiReceiptViewImageDialog,
  );
  const loading = useAppSelector((state) => state.item.loading);
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const settings = {
    dots: true,
    infinite: true,
    autoplay: false,
    centerMode: true,
    centerPadding: "0px",
    autoplaySpeed: 2000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    draggable: true,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  const handleOpenImage = (url: string) => {
    if (url && typeof url === "string") {
      window.open(url);
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <CustomAppDialog open={BandhaiReceiptViewImageDialog}>
        {/* Image Gallery */}
        <div style={{ width: "35vw", height: "50vh" }}>
          <GridLayout gap={{ rows: 10, cols: 10 }}>
            <GridLayoutItem
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography.h5 style={{ margin: "0" }}>
                {"Product Images"}
              </Typography.h5>
              <ImCross
                style={{
                  color: "white",
                  background: "red",
                  borderRadius: "50%",
                  padding: "5px",
                  fontSize: "24px",
                }}
                onClick={() => {
                  dispatch(closeBandhaiReceiptViewImageDialog());
                  dispatch(setOrderImageViewPayload(null));
                }}
              />
            </GridLayoutItem>
            <GridLayoutItem>
              {processByImageList && processByImageList?.length === 0 && (
                <AlertBox alert="No Image Available!" />
              )}
            </GridLayoutItem>
            <GridLayoutItem
              style={{ display: "flex", justifyContent: "center" }}
            >
              <div
                className="slider-container product-view-images"
                style={{ width: "30vw" }}
              >
                {processByImageList && processByImageList.length > 1 ? (
                  <Slider {...settings}>
                    {processByImageList &&
                      processByImageList.length > 0 &&
                      processByImageList.map((image: any, index: number) => (
                        <div>
                          <div key={index} style={{ height: 300 }}>
                            <img
                              src={image.image_name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                              onClick={() => handleOpenImage(image.image_name)}
                            />
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "10px",
                              gap: 5,
                            }}
                          >
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Title
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.title || "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Process Name
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.process_name || "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Main Image
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.isreadyimage ? "YES" : "NO"}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </Slider>
                ) : (
                  <>
                    {processByImageList &&
                      processByImageList.length > 0 &&
                      processByImageList.map((image: any, index: number) => (
                        <div>
                          <div key={index} style={{ height: 300 }}>
                            <img
                              src={image.image_name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                              onClick={() => handleOpenImage(image.image_name)}
                            />
                          </div>
                          {/* <Typography.h5 style={{ textAlign: "center" }}>
                                {image.title}
                              </Typography.h5> */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "10px",
                              gap: 5,
                            }}
                          >
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Title
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.title || "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Process Name
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.process_name || "N/A"}
                              </p>
                            </div>
                            <div
                              style={{
                                background: "#e8e8e8",
                                padding: "5px 10px",
                                borderRadius: "4px",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 8px 0",
                                  fontSize: "14px",
                                  color: "#718096",
                                  fontWeight: 500,
                                }}
                              >
                                Main Image
                              </p>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  color: "#2d3748",
                                  fontWeight: 600,
                                }}
                              >
                                {image.isreadyimage ? "YES" : "NO"}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </>
                )}
              </div>
            </GridLayoutItem>
          </GridLayout>
        </div>
      </CustomAppDialog>
    </>
  );
};

export default BandhaiReceiptOrderEntryArray;
